/**
 * Sidebar — manifest-driven navigation (Benevita Design System v1.0).
 *
 * Desktop: fixed-width collapsible panel (68 / 248 px).
 * Mobile:  rendered inside a slide-in drawer by Layout.js (receives onClose).
 *
 * UX features:
 *  - Lucide icons (consistent stroke) with emoji fallback for unknown routes
 *  - Collapsible groups with chevrons; expanded state persisted per user
 *  - Quick-find filter box (Ctrl+K-style muscle memory, no modal)
 *  - Group containing the active route auto-expands
 *  - Pill-style active state with sky accent on navy
 */

import React, { useState, useMemo, useEffect } from 'react';
import BenevitaLogo from '../BenevitaLogo';
import { NavLink, useNavigate, useLocation } from 'react-router-dom';
import {
  LayoutDashboard, BarChart3, Stethoscope, HeartPulse, Building2,
  ClipboardList, Ambulance, Radar, CalendarDays, FileText, Microscope,
  Pill, Activity, Wallet, ShieldCheck, Shield, UserRound, KeyRound,
  FolderTree, Link2, ScrollText, Lock, Map, Settings2, Globe, Image,
  Search, ChevronDown, ChevronRight, ChevronsLeft, ChevronsRight,
  LogOut, X, Contact, Plus,
} from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useRbac } from '../../context/RbacContext';
import { can } from '../../utils/permissions';
import useIsMobile from '../../hooks/useIsMobile';

// ── Design tokens (BDS navy surface) ─────────────────────────────────────────
const SB = {
  bg:          '#ffffff',
  bgHover:     'rgba(123,30,60,0.055)',
  bgActive:    'rgba(123,30,60,0.10)',
  border:      '#efe6ea',
  text:        '#5e4a53',
  textMuted:   '#9a8791',
  groupLabel:  '#9a7634',
  accent:      '#7b1e3c',
  accentSoft:  'rgba(123,30,60,0.45)',
  inputBg:     '#f7f0f3',
  userBg:      'rgba(123,30,60,0.045)',
  danger:      '#ef9a9a',
  dangerBg:    'rgba(211,47,47,0.12)',
  dangerBorder:'rgba(211,47,47,0.25)',
};

// ── Route → Lucide icon map (fallback: manifest emoji) ───────────────────────
const ICONS = {
  '/dashboard':            LayoutDashboard,
  '/analytics':            BarChart3,
  '/ss/parties':           Contact,
  '/ss/parties?create=1':  Plus,
  '/ss/parties?role_code=PATIENT': HeartPulse,
  '/ss/nurses':            Stethoscope,
  '/ss/doctors':           Stethoscope,
  '/ss/companies':         Building2,
  '/referrals':            ClipboardList,
  '/care-requests':        Ambulance,
  '/dispatch':             Radar,
  '/visits':               CalendarDays,
  '/care-plans':           FileText,
  '/clinical':             Microscope,
  '/prescriptions':        Pill,
  '/remote-obs':           Activity,
  '/billing':              Wallet,
  '/compliance':           ShieldCheck,
  '/rbac':                 Shield,
  '/ss/users':             UserRound,
  '/ss/roles':             Shield,
  '/ss/permissions':       KeyRound,
  '/ss/org-units':         FolderTree,
  '/ss/relationships':     Link2,
  '/ss/audit':             ScrollText,
  '/ss/security-catalog':  Lock,
  '/ss/navigation':        Map,
  '/ss/lookup-config':     Settings2,
  '/settings/website':     Globe,
  '/settings/media':       Image,
};

// ── Badge store: route → badge config (count + tier) ─────────────────────────
let _badgeCache = {};
export function setBadges(badges) {
  _badgeCache = { ..._badgeCache, ...badges };
}

const BADGE_COLOR = {
  critical: { bg: '#d32f2f', text: '#fff' },
  warning:  { bg: '#f57c00', text: '#fff' },
  info:     { bg: '#7b1e3c', text: '#fff' },
};

// ── Hard-coded fallback nav (mirrors rbac.yaml nav section) ──────────────────
const FALLBACK_GROUPS = [
  {
    group_label: 'Overview',
    group_order: 0,
    items: [
      { route: '/dashboard', icon: '📊', item_label: 'Dashboard', permission_key: 'page.dashboard' },
      { route: '/analytics',       icon: '📈', item_label: 'Analytics',       permission_key: 'page.analytics' },
      { route: '/risk-dashboard',  icon: '🧠', item_label: 'Risk Dashboard',   permission_key: 'page.analytics', badge_key: 'risk_critical', badge_tier: 'critical' },
    ],
  },
  {
    group_label: 'Parties',
    group_order: 10,
    items: [
      { route: '/ss/parties',                   icon: '🪪', item_label: 'Party Explorer', permission_key: 'page.ss_parties' },
      { route: '/ss/parties?create=1',          icon: '➕', item_label: 'New Party',      permission_key: 'page.ss_parties' },
      { route: '/ss/parties?role_code=PATIENT', icon: '🏥', item_label: 'Patients',       permission_key: 'page.ss_parties' },
      { route: '/ss/nurses',                    icon: '🩺', item_label: 'Nurses',         permission_key: 'page.ss_nurses' },
      { route: '/ss/doctors',                   icon: '⚕️', item_label: 'Doctors',        permission_key: 'page.ss_doctors' },
      { route: '/ss/companies',                 icon: '🏢', item_label: 'Organizations',  permission_key: 'page.ss_companies' },
    ],
  },
  {
    group_label: 'Care Operations',
    group_order: 20,
    items: [
      { route: '/referrals',     icon: '📋', item_label: 'Referrals',       permission_key: 'page.referrals',  badge_key: 'referrals_pending' },
      { route: '/care-requests', icon: '🚑', item_label: 'Care Requests',   permission_key: 'page.requests',   badge_key: 'requests_critical', badge_tier: 'critical' },
      { route: '/dispatch',      icon: '📡', item_label: 'Dispatch Engine', permission_key: 'page.dispatch' },
      { route: '/branches',      icon: '🏬', item_label: 'Branches',        permission_key: 'page.dispatch' },
      { route: '/visits',            icon: '📅', item_label: 'Visits',          permission_key: 'page.visits', badge_key: 'visits_overdue', badge_tier: 'warning' },
      { route: '/visit-calendar',   icon: '🗓️', item_label: 'Visit Calendar',   permission_key: 'page.visits' },
      { route: '/recurring-visits', icon: '🔁', item_label: 'Recurring Series', permission_key: 'page.visits' },
      { route: '/care-plans',       icon: '📝', item_label: 'Care Plans',       permission_key: 'page.care_plans' },
    ],
  },
  {
    group_label: 'Clinical',
    group_order: 30,
    items: [
      { route: '/clinical',      icon: '🔬', item_label: 'Clinical Reports',  permission_key: 'page.clinical' },
      { route: '/oasis',         icon: '📋', item_label: 'OASIS-E',           permission_key: 'page.clinical' },
      { route: '/prescriptions', icon: '💊', item_label: 'Prescriptions',     permission_key: 'page.prescriptions' },
      { route: '/remote-obs',    icon: '📟', item_label: 'Remote Monitoring', permission_key: 'page.rpm' },
    ],
  },
  {
    group_label: 'Finance & Admin',
    group_order: 40,
    items: [
      { route: '/billing',        icon: '💰', item_label: 'Billing',          permission_key: 'page.billing' },
      { route: '/revenue-cycle',  icon: '📊', item_label: 'Revenue Cycle',   permission_key: 'page.billing' },
      { route: '/compliance', icon: '🔒', item_label: 'Compliance & Audit',  permission_key: 'page.audit' },
      { route: '/rbac',       icon: '🛡️', item_label: 'Roles & Permissions', permission_key: 'page.rbac' },
    ],
  },
  {
    group_label: 'Identity Platform',
    group_order: 50,
    items: [
      { route: '/ss/users',            icon: '👤', item_label: 'User Accounts',    permission_key: 'page.ss_users' },
      { route: '/ss/roles',            icon: '🛡️', item_label: 'Roles & Perms',    permission_key: 'page.ss_roles' },
      { route: '/ss/permissions',      icon: '🔑', item_label: 'Permissions',      permission_key: 'page.ss_permissions' },
      { route: '/ss/org-units',        icon: '🗂️', item_label: 'Org Units',        permission_key: 'page.ss_org_units' },
      { route: '/ss/relationships',    icon: '🔗', item_label: 'Relationships',    permission_key: 'page.ss_relationships' },
      { route: '/ss/audit',            icon: '📜', item_label: 'Audit Log',        permission_key: 'page.ss_audit' },
      { route: '/ss/security-catalog', icon: '🔐', item_label: 'Security Catalog', permission_key: 'page.ss_security_catalog' },
      { route: '/ss/navigation',       icon: '🗺️', item_label: 'Navigation Admin', permission_key: 'page.ss_navigation' },
      { route: '/ss/lookup-config',    icon: '⚙️',  item_label: 'Lookup Config',    permission_key: 'page.ss_lookup_config' },
    ],
  },
  {
    group_label: 'Settings',
    group_order: 60,
    items: [
      { route: '/settings/website', icon: '🌐', item_label: 'Website Content', permission_key: 'marketing.config.read' },
      { route: '/settings/media',   icon: '🖼️', item_label: 'Media Library',   permission_key: 'marketing.config.write' },
    ],
  },
];

// Groups expanded by default for first-time users.
const DEFAULT_EXPANDED = ['Overview', 'Care Operations'];
const LS_KEY = 'hhms.sidebar.groups';

function loadExpanded() {
  try {
    const raw = localStorage.getItem(LS_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  return DEFAULT_EXPANDED;
}

function ItemIcon({ route, emoji, size = 17, color }) {
  const Icon = ICONS[route];
  if (Icon) return <Icon size={size} strokeWidth={1.8} color={color} style={{ flexShrink: 0 }} />;
  return <span style={{ fontSize: size - 2, lineHeight: 1, flexShrink: 0 }}>{emoji || '•'}</span>;
}

export default function Sidebar({ onClose }) {
  const { user, logout } = useAuth();
  const { nav: manifestNav, loading: rbacLoading } = useRbac();
  const navigate  = useNavigate();
  const location  = useLocation();
  const isMobile  = useIsMobile();

  const [collapsed, setCollapsed] = useState(false);
  const [filter, setFilter]       = useState('');
  const [expanded, setExpanded]   = useState(loadExpanded);

  const isCollapsed = !isMobile && collapsed;
  const searching   = filter.trim().length > 0;

  const handleLogout = () => { logout(); navigate('/login'); if (onClose) onClose(); };
  const handleNav    = () => { if (isMobile && onClose) onClose(); };
  const userRoles    = user?.roles || [];

  // ── Resolve nav groups (manifest first, fallback second) ──────────────────
  const visibleGroups = useMemo(() => {
    if (!rbacLoading && manifestNav && manifestNav.length > 0) {
      return manifestNav.filter((g) => g.items && g.items.length > 0);
    }
    return FALLBACK_GROUPS
      .map((g) => ({
        ...g,
        items: g.items.filter((it) =>
          it.permission_key ? can(userRoles, it.permission_key) !== false : true
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [rbacLoading, manifestNav, userRoles]);

  const normItem = (it) => ({
    path:  it.route || it.path || '/',
    icon:  it.icon  || '•',
    label: it.label || it.item_label || it.route || '/',
    badge_key:  it.badge_key,
    badge_tier: it.badge_tier,
  });

  // ── Filtered view when searching ───────────────────────────────────────────
  const groupsToRender = useMemo(() => {
    if (!searching) return visibleGroups;
    const q = filter.trim().toLowerCase();
    return visibleGroups
      .map((g) => ({
        ...g,
        items: (g.items || []).filter((it) =>
          normItem(it).label.toLowerCase().includes(q)
        ),
      }))
      .filter((g) => g.items.length > 0);
  }, [visibleGroups, searching, filter]);

  // ── Auto-expand the group containing the active route ─────────────────────
  useEffect(() => {
    const current = location.pathname + location.search;
    for (const g of visibleGroups) {
      const label = g.group_label || g.label || '';
      const hit = (g.items || []).some((it) => {
        const p = normItem(it).path;
        return current === p || (p !== '/' && location.pathname.startsWith(p.split('?')[0]) && p.split('?')[0] !== '/');
      });
      if (hit && !expanded.includes(label)) {
        setExpanded((prev) => {
          const next = [...prev, label];
          try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
          return next;
        });
        break;
      }
    }
  }, [location.pathname, location.search, visibleGroups]);

  const toggleGroup = (label) => {
    setExpanded((prev) => {
      const next = prev.includes(label) ? prev.filter((l) => l !== label) : [...prev, label];
      try { localStorage.setItem(LS_KEY, JSON.stringify(next)); } catch { /* ignore */ }
      return next;
    });
  };

  const userInitial = (user?.name || user?.username || 'A')[0].toUpperCase();
  const userName    = user?.name || user?.username || 'User';
  const userRole    = userRoles.slice(0, 2).join(' · ') || 'Staff';

  return (
    <aside style={{
      width:         isCollapsed ? 68 : 248,
      minHeight:     '100vh',
      background:    SB.bg,
      color:         SB.text,
      display:       'flex',
      flexDirection: 'column',
      fontFamily:    'Inter, sans-serif',
      flexShrink:    0,
      transition:    'width 0.22s cubic-bezier(0.4,0,0.2,1)',
      overflow:      'hidden',
      borderRight:   `1px solid ${SB.border}`,
    }}>

      {/* ── Brand header ── */}
      <div style={{
        padding:        isCollapsed ? '16px 0' : '16px 14px',
        display:        'flex',
        alignItems:     'center',
        justifyContent: isCollapsed ? 'center' : 'space-between',
        gap: 8,
        flexShrink: 0,
      }}>
        {!isCollapsed ? (
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
            <BenevitaLogo size={32} variant="mark" />
            <div style={{ minWidth: 0 }}>
              <div style={{ fontFamily: 'Georgia, serif', fontSize: 18, fontWeight: 400, color: SB.accent, letterSpacing: '-0.3px', lineHeight: 1.05 }}>
                benevita
              </div>
              <div style={{ fontSize: 8.5, color: SB.groupLabel, marginTop: 2, letterSpacing: '1.5px', textTransform: 'uppercase' }}>
                Home Health Care
              </div>
            </div>
          </div>
        ) : (
          <BenevitaLogo size={32} variant="mark" />
        )}

        {!isMobile && !isCollapsed && (
          <button
            onClick={() => setCollapsed(true)}
            className="hhms-sb-iconbtn"
            title="Collapse sidebar"
            style={iconBtnStyle}
          >
            <ChevronsLeft size={16} />
          </button>
        )}
        {isMobile && onClose && (
          <button onClick={onClose} aria-label="Close navigation" style={{ ...iconBtnStyle, marginLeft: 'auto' }}>
            <X size={18} />
          </button>
        )}
      </div>

      {/* ── Expand button (collapsed mode) ── */}
      {isCollapsed && (
        <button
          onClick={() => setCollapsed(false)}
          className="hhms-sb-iconbtn"
          title="Expand sidebar"
          style={{ ...iconBtnStyle, margin: '0 auto 6px' }}
        >
          <ChevronsRight size={16} />
        </button>
      )}

      {/* ── Quick find ── */}
      {!isCollapsed && (
        <div style={{ padding: '0 12px 10px', flexShrink: 0 }}>
          <div style={{
            display: 'flex', alignItems: 'center', gap: 8,
            background: SB.inputBg,
            border: `1px solid ${SB.border}`,
            borderRadius: 8,
            padding: '7px 10px',
          }}>
            <Search size={14} color={SB.groupLabel} style={{ flexShrink: 0 }} />
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Find a page…"
              style={{
                background: 'none', border: 'none', outline: 'none',
                color: SB.text, fontSize: 12.5, width: '100%',
                fontFamily: 'Inter, sans-serif',
              }}
            />
            {searching && (
              <button onClick={() => setFilter('')} style={{ ...iconBtnStyle, padding: 0 }} aria-label="Clear search">
                <X size={13} />
              </button>
            )}
          </div>
        </div>
      )}

      {/* ── Nav groups ── */}
      <div className="hhms-sb-scroll" style={{
        flex: 1, overflowY: 'auto', overflowX: 'hidden',
        padding: isCollapsed ? '4px 8px' : '2px 10px 8px',
      }}>
        {groupsToRender.map((group) => {
          const groupLabel = group.group_label || group.label || '';
          const isOpen = searching || isCollapsed || expanded.includes(groupLabel);
          return (
            <div key={groupLabel} style={{ marginBottom: isCollapsed ? 0 : 2 }}>
              {/* Group header (hidden when collapsed; divider instead) */}
              {!isCollapsed ? (
                <button
                  onClick={() => !searching && toggleGroup(groupLabel)}
                  style={{
                    display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                    width: '100%', background: 'none', border: 'none', cursor: searching ? 'default' : 'pointer',
                    padding: '10px 8px 5px', color: SB.groupLabel,
                    fontSize: 10, fontWeight: 700, letterSpacing: '1px',
                    textTransform: 'uppercase', fontFamily: 'Inter, sans-serif',
                  }}
                >
                  <span>{groupLabel}</span>
                  {!searching && (isOpen
                    ? <ChevronDown size={12} color={SB.groupLabel} />
                    : <ChevronRight size={12} color={SB.groupLabel} />)}
                </button>
              ) : (
                <div style={{ borderTop: `1px solid ${SB.border}`, margin: '6px 6px' }} />
              )}

              {/* Items */}
              {isOpen && (group.items || []).map((rawItem) => {
                const { path, icon, label, badge_key, badge_tier } = normItem(rawItem);
                const badgeCount = badge_key ? (_badgeCache[badge_key] || 0) : 0;
                const bc = BADGE_COLOR[badge_tier || 'info'] || BADGE_COLOR.info;

                return (
                  <NavLink
                    key={path}
                    to={path}
                    onClick={handleNav}
                    title={isCollapsed ? (badgeCount > 0 ? `${label} (${badgeCount})` : label) : undefined}
                    className="hhms-sb-item"
                    style={({ isActive }) => ({
                      display:        'flex',
                      alignItems:     'center',
                      gap:            isCollapsed ? 0 : 11,
                      padding:        isCollapsed ? '10px 0' : '7.5px 10px',
                      borderRadius:   8,
                      marginBottom:   1,
                      textDecoration: 'none',
                      fontSize:       12.5,
                      fontWeight:     isActive ? 600 : 450,
                      color:          isActive ? SB.accent : SB.text,
                      background:     isActive ? SB.bgActive : 'transparent',
                      justifyContent: isCollapsed ? 'center' : 'flex-start',
                      whiteSpace:     'nowrap',
                      position:       'relative',
                      transition:     'background 0.13s, color 0.13s',
                    })}
                  >
                    {({ isActive }) => (
                      <>
                        <span style={{ display: 'inline-flex', position: 'relative' }}>
                          <ItemIcon
                            route={path}
                            emoji={icon}
                            color={isActive ? SB.accent : SB.accentSoft}
                          />
                          {isCollapsed && badgeCount > 0 && (
                            <span style={{
                              position: 'absolute', top: -3, right: -5,
                              width: 7, height: 7, borderRadius: '50%',
                              background: bc.bg, border: `1.5px solid ${SB.bg}`,
                            }} />
                          )}
                        </span>
                        {!isCollapsed && (
                          <>
                            <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', flex: 1 }}>
                              {label}
                            </span>
                            {badgeCount > 0 && (
                              <span style={{
                                fontSize: 9.5, fontWeight: 700, padding: '1px 7px',
                                borderRadius: 20, background: bc.bg, color: bc.text,
                                flexShrink: 0, lineHeight: '15px',
                              }}>
                                {badgeCount > 99 ? '99+' : badgeCount}
                              </span>
                            )}
                          </>
                        )}
                      </>
                    )}
                  </NavLink>
                );
              })}
            </div>
          );
        })}

        {groupsToRender.length === 0 && !isCollapsed && (
          <div style={{ color: SB.groupLabel, fontSize: 12, padding: 14, textAlign: 'center' }}>
            {rbacLoading ? 'Loading navigation…' : (searching ? 'No pages match.' : 'No pages available.')}
          </div>
        )}
      </div>

      {/* ── User footer ── */}
      <div style={{
        padding: isCollapsed ? '10px 8px 14px' : '10px 12px 14px',
        borderTop: `1px solid ${SB.border}`,
        flexShrink: 0,
      }}>
        {!isCollapsed ? (
          <div style={{
            display: 'flex', alignItems: 'center', gap: 10,
            padding: '9px 10px', background: SB.userBg, borderRadius: 10,
          }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%', flexShrink: 0,
              background: 'linear-gradient(135deg,#7b1e3c,#c39a4e)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, color: '#fff',
            }}>
              {userInitial}
            </div>
            <div style={{ overflow: 'hidden', minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12.5, fontWeight: 600, color: '#e8f0fa', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {userName}
              </div>
              <div style={{ fontSize: 10, color: SB.groupLabel, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                {userRole}
              </div>
            </div>
            <button
              onClick={handleLogout}
              title="Sign out"
              className="hhms-sb-logout"
              style={{
                background: 'none',
                border: 'none',
                color: SB.danger,
                cursor: 'pointer',
                padding: 6,
                borderRadius: 7,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                flexShrink: 0,
                transition: 'background .15s',
              }}
            >
              <LogOut size={15} />
            </button>
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
            <div style={{
              width: 32, height: 32, borderRadius: '50%',
              background: 'linear-gradient(135deg,#7b1e3c,#c39a4e)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              fontSize: 13, fontWeight: 700, color: '#fff',
            }} title={userName}>
              {userInitial}
            </div>
            <button
              onClick={handleLogout}
              title="Sign out"
              className="hhms-sb-logout"
              style={{
                background: SB.dangerBg, border: `1px solid ${SB.dangerBorder}`,
                color: SB.danger, borderRadius: 7, cursor: 'pointer',
                width: 32, height: 26, display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <LogOut size={13} />
            </button>
          </div>
        )}
      </div>

      {/* Scoped styles: thin scrollbar + hover states */}
      <style>{`
        .hhms-sb-scroll::-webkit-scrollbar { width: 5px; }
        .hhms-sb-scroll::-webkit-scrollbar-track { background: transparent; }
        .hhms-sb-scroll::-webkit-scrollbar-thumb { background: rgba(217,140,179,0.15); border-radius: 3px; }
        .hhms-sb-scroll::-webkit-scrollbar-thumb:hover { background: rgba(217,140,179,0.3); }
        .hhms-sb-item:hover { background: ${SB.bgHover} !important; }
        .hhms-sb-iconbtn:hover { color: ${SB.accent} !important; background: ${SB.bgHover}; }
        .hhms-sb-logout:hover { background: ${SB.dangerBg} !important; }
      `}</style>
    </aside>
  );
}

const iconBtnStyle = {
  background: 'none',
  border: 'none',
  color: '#8b7681',
  cursor: 'pointer',
  padding: 5,
  borderRadius: 6,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 1,
  transition: 'color .15s, background .15s',
};
